/**
 * ServerMonitorPage — 系统 · 服务器监控（root/admin 只读）
 *
 * C 端 server Workers Logs 报错监控：核心流程健康五卡 · 告警（活跃 / 24h 已恢复）· 告警规则 · 飞书通知与自检 ·
 * 错误日志查询（Telemetry，cursor 翻页 + 详情抽屉）。转写自原型 server-monitor.jsx；与原型唯一偏离：
 * 告警「日志」按钮不开抽屉，改为把下方日志搜索设为该告警指纹并滚动过去（后端按指纹精确过滤）。
 *
 * 数据：GET /api/v1/server-monitor/overview（60s 刷新）+ /logs（筛选全部下推服务端）；健康卡点击 → 类别筛选。
 * 支付到账时效（同一 cron 的 DB 数据源）：规则卡追加三条时效规则，告警列表混排时效告警（provider + 指标，无「日志」下钻），
 * 「通知与自检」卡展示最近一轮各 provider 的 p95 / 积压 / 样本。
 * C 端接口时效（同一 cron 的 Telemetry calculations）：「接口时效」卡列近窗口 p95 最慢的接口（前 20）与队列，
 * 超阈值行用级别 chip 标注；规则卡追加三条接口时效规则；告警列表混排接口时效告警，同样无「日志」下钻——
 * 慢请求是 info 级 request.complete，错误日志表只收 error / warn，按 trigger 下钻只会看到无关报错或空表。
 * 用户行为异常（同一 cron 的疑似爬取评估）：「疑似爬取用户」卡列近 24h 按 userId 达阈值的用户（GET /user-crawl，独立 query，
 * 与 overview 同频刷新），行内可复制按 path 计数的追查命令；版式沿用「接口时效」卡。
 * 环境：首次不带 service，由后端取本环境目标；「生产 / 开发」切换只列 overview.viewable_services，
 * 只有一项（dev 后台）时不显示切换。
 */

import {
  SERVER_MONITOR_CATEGORIES,
  SERVER_MONITOR_LOG_PAGE_LIMIT,
  SERVER_MONITOR_LOG_RANGES,
  SERVER_MONITOR_ONGOING_RENOTIFY_MAX_MINUTES,
  SERVER_MONITOR_RATE_LIMIT_DEGRADED_EVENT,
  SERVER_MONITOR_RATE_LIMIT_DEGRADED_QUERY_FAILED_EVENT,
  type ServerMonitorCategory,
  type ServerMonitorLogLevel,
  type ServerMonitorLogRange,
  type ServerMonitorService,
} from '@novel-admin/shared';
import { useEffect, useRef, useState } from 'react';
import { AdminApiError } from '~/api/errors';
import {
  type AlertRow,
  type LogRow,
  SERVER_MONITOR_CATEGORY_META,
  buildLogQueryCommand,
  feishuGroupName,
  rangeCoveringAlert,
  toAlertRow,
  toApiLatencyView,
  toEvalStatus,
  toHealthCardRows,
  toLogRow,
  toNotificationView,
  toPaymentLatencyView,
  toRuleRows,
  toServiceOptions,
} from '~/data/adapters/server-monitor-adapter';
import { toUserCrawlView } from '~/data/adapters/server-monitor-user-crawl-adapter';
import {
  useServerMonitorLogs,
  useServerMonitorOverview,
  useServerMonitorUserCrawl,
} from '~/data/queries';
import { Icons } from '~/lib/icons';
import { EmptyBlock, ErrorBlock } from '~/pages/metrics/metrics-shared';
import { FilterButton } from '~/ui/FilterButton';
import { Pagination } from '~/ui/Pagination';
import { Row } from '~/ui/Pair';
import { SearchInput } from '~/ui/SearchInput';
import { Section } from '~/ui/Section';
import { KpiSkeleton, SectionSkeleton, TableSkeleton } from '~/ui/Skeleton';
import { Table } from '~/ui/Table';
import { toast } from '~/ui/Toast';
import {
  AlertItem,
  CatChip,
  HealthCard,
  LevelChip,
  LogDrawer,
  LvChip,
} from './server-monitor-parts';

/** 搜索框输入防抖（needle 下推 Telemetry，逐字发请求无意义）*/
const SEARCH_DEBOUNCE_MS = 400;

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

const LEVEL_OPTIONS = [
  { value: 'error', label: 'error' },
  { value: 'warn', label: 'warn' },
];
const CATEGORY_OPTIONS = SERVER_MONITOR_CATEGORIES.map((c) => ({
  value: c,
  label: SERVER_MONITOR_CATEGORY_META[c].label,
}));

export default function ServerMonitorPage() {
  // null = 本环境缺省目标（后端决定）；切换后为所选 service
  const [selectedService, setSelectedService] = useState<ServerMonitorService | null>(null);
  const [tab, setTab] = useState<'active' | 'recovered'>('active');
  const [range, setRange] = useState<ServerMonitorLogRange>('1h');
  const [lvF, setLvF] = useState<ServerMonitorLogLevel[]>(['error']);
  const [catF, setCatF] = useState<ServerMonitorCategory[]>([]);
  const [q, setQ] = useState('');
  const debouncedQ = useDebounced(q, SEARCH_DEBOUNCE_MS);
  // cursor 栈：pageCursors[i] = 第 i+1 页的 cursor（第 1 页为 null）
  const [pageCursors, setPageCursors] = useState<Array<string | null>>([null]);
  const [page, setPage] = useState(1);
  const [drawer, setDrawer] = useState<LogRow | null>(null);
  const logsRef = useRef<HTMLDivElement>(null);

  const overviewQ = useServerMonitorOverview(selectedService);
  const overview = overviewQ.data;
  const service = overview?.service ?? selectedService;
  const serviceOptions = toServiceOptions(overview?.viewable_services ?? []);
  const nowMs = overviewQ.dataUpdatedAt || Date.now();
  const userCrawlQ = useServerMonitorUserCrawl(selectedService);
  const userCrawl = userCrawlQ.data
    ? toUserCrawlView(userCrawlQ.data, userCrawlQ.dataUpdatedAt || Date.now())
    : null;

  // 任一筛选维度变化 → 回第 1 页（cursor 与筛选绑定）
  // biome-ignore lint/correctness/useExhaustiveDependencies: 专门在筛选维度变化时重置 cursor 栈
  useEffect(() => {
    setPageCursors([null]);
    setPage(1);
  }, [service, range, lvF, catF, debouncedQ]);

  const logsQ = useServerMonitorLogs({
    service,
    range,
    levels: lvF,
    categories: catF,
    q: debouncedQ,
    cursor: pageCursors[page - 1] ?? null,
  });
  const logRows = (logsQ.data?.items ?? []).map((item) => toLogRow(item, Date.now()));
  // keepPreviousData 期间 data 仍是上一页的响应：其 next_cursor 属于上一页，翻页须等新页落地
  const nextCursor = logsQ.isPlaceholderData ? null : (logsQ.data?.next_cursor ?? null);

  const changePage = (p: number) => {
    if (p === page + 1 && nextCursor) {
      setPageCursors((prev) => [...prev.slice(0, page), nextCursor]);
      setPage(p);
      return;
    }
    if (p >= 1 && p <= pageCursors.length) setPage(p);
  };

  const pickCategory = (category: ServerMonitorCategory) => {
    setCatF([category]);
    toast(`已按「${SERVER_MONITOR_CATEGORY_META[category].label}」筛选错误日志`, { tone: 'info' });
  };

  const showAlertLogs = (alert: AlertRow) => {
    setQ(alert.fingerprint);
    setCatF([]);
    // 告警由 error 级事件产生，唯一例外是 warn 级的同一主体降级放行突增：按告警事件选级别，否则触发记录会被滤掉
    setLvF(
      alert.fingerprint.startsWith(`${SERVER_MONITOR_RATE_LIMIT_DEGRADED_EVENT}|`)
        ? ['warn']
        : ['error'],
    );
    setRange((current) => rangeCoveringAlert(alert.lastSeenAt, current, Date.now()));
    logsRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };

  const thresholds = overview?.rules.thresholds;
  const evalStatus = overview ? toEvalStatus(overview, nowMs) : null;
  const notification = overview ? toNotificationView(overview, nowMs) : null;
  const paymentLatency = overview ? toPaymentLatencyView(overview, nowMs) : null;
  const apiLatency = overview ? toApiLatencyView(overview, nowMs) : null;
  const healthState = overview?.health.state;
  const alertCtx = {
    nowMs,
    windowMinutes: thresholds?.window_minutes ?? 10,
    cooldownMinutes: thresholds?.cooldown_minutes ?? 30,
  };
  const activeAlerts = (overview?.alerts?.active ?? []).map((a) => toAlertRow(a, alertCtx));
  const recoveredAlerts = (overview?.alerts?.recovered ?? []).map((a) => toAlertRow(a, alertCtx));
  const shownAlerts = tab === 'active' ? activeAlerts : recoveredAlerts;
  const overviewError = overviewQ.isError && !overview ? (overviewQ.error as Error | null) : null;

  const logsError = logsQ.isError ? (logsQ.error as Error | null) : null;
  // 未配置以 overview.health.state 为准（logs 此时返 503 SERVICE_UNAVAILABLE，与 Telemetry 故障同码）
  const logsUnconfigured = healthState === 'unconfigured';
  // 本环境目标未知（概览未返回）时日志查询未启用：按加载中展示，不显示「没有错误日志」
  const logsPending = logsQ.isLoading || service === null;

  return (
    <div className="page" data-screen-label="服务器监控">
      <div className="page-header">
        <div>
          <h1 className="page-title">服务器监控</h1>
          <div className="page-sub hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
            {service ? (
              <>
                <span className="mono">{service}</span> · Workers Logs 每 2 分钟评估 ·
                告警推送飞书「
                {feishuGroupName(service)}」群
              </>
            ) : (
              'Workers Logs 每 2 分钟评估 · 告警推送飞书'
            )}
          </div>
        </div>
        <div className="hstack" style={{ gap: 8 }}>
          {serviceOptions.length > 1 && (
            <div className="seg">
              {serviceOptions.map((option) => (
                <button
                  key={option.service}
                  type="button"
                  className={service === option.service ? 'on' : ''}
                  onClick={() => setSelectedService(option.service)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
          {evalStatus && (
            <span
              className={`chip ${evalStatus.tone === 'default' ? '' : `chip-${evalStatus.tone}`}`}
              style={{ fontSize: 10.5 }}
              title={evalStatus.title}
            >
              {evalStatus.tone === 'pos' ? (
                <Icons.IconCheck size={10} />
              ) : evalStatus.tone === 'default' ? null : (
                <Icons.IconAlert size={10} />
              )}
              {evalStatus.label}
            </span>
          )}
          <button
            type="button"
            className="btn"
            title="立即刷新"
            aria-label="立即刷新"
            disabled={overviewQ.isFetching}
            onClick={() => {
              void overviewQ.refetch();
              if (service) void logsQ.refetch();
            }}
          >
            <Icons.IconRefresh size={13} />
          </button>
        </div>
      </div>

      {overviewQ.isLoading ? (
        <>
          <KpiSkeleton count={5} />
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0,1.25fr) minmax(0,1fr)',
              gap: 12,
            }}
          >
            <SectionSkeleton rows={4} />
            <SectionSkeleton rows={4} />
          </div>
        </>
      ) : overviewError || !overview ? (
        <ErrorBlock
          title="服务器监控概览加载失败"
          note={overviewError?.message ?? 'GET /api/v1/server-monitor/overview 失败 · 请重试'}
          requestId={overviewError instanceof AdminApiError ? overviewError.requestId : undefined}
          minHeight={200}
          onRetry={() => void overviewQ.refetch()}
        />
      ) : (
        <>
          {healthState === 'ok' ? (
            <div
              style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0,1fr))', gap: 12 }}
            >
              {toHealthCardRows(overview, nowMs).map((h) => (
                <HealthCard key={h.category} h={h} onPick={() => pickCategory(h.category)} />
              ))}
            </div>
          ) : healthState === 'unconfigured' ? (
            <EmptyBlock
              minHeight={120}
              title="日志查询未配置"
              note="admin Worker 缺少 Workers Observability Telemetry token 或 account，健康卡与错误日志不可用；告警与自检仍按 cron 状态展示。"
            />
          ) : (
            <ErrorBlock
              minHeight={120}
              title="健康数据查询失败"
              note="Workers Telemetry 暂不可用，健康卡未刷新 · 告警与自检仍来自 cron 状态"
              onRetry={() => void overviewQ.refetch()}
            />
          )}
          {overview.health.truncated && (
            <div style={{ fontSize: 11, color: 'var(--warn)', marginTop: -4 }}>
              近 24h error 事件超过 5000 条，已截断：健康卡计数偏小
            </div>
          )}
          {overview.health.sampled && (
            <div style={{ fontSize: 11, color: 'var(--warn)', marginTop: -4 }}>
              近 24h 日志被 Cloudflare 采样：健康卡计数不完整，「正常」不代表没有错误
            </div>
          )}

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0,1.25fr) minmax(0,1fr)',
              gap: 12,
            }}
          >
            {/* 告警 */}
            <div
              className="card"
              style={{ overflow: 'hidden', minWidth: 0, display: 'flex', flexDirection: 'column' }}
            >
              <div
                className="hstack"
                style={{
                  justifyContent: 'space-between',
                  padding: '12px 14px',
                  flexWrap: 'wrap',
                  gap: 8,
                }}
              >
                <div>
                  <span style={{ fontWeight: 500, fontSize: 13.5 }}>告警</span>
                  <div style={{ color: 'var(--fg-muted)', fontSize: 11.5, marginTop: 2 }}>
                    同指纹持续中按 {alertCtx.cooldownMinutes} 分钟起翻倍间隔重推（上限{' '}
                    {SERVER_MONITOR_ONGOING_RENOTIFY_MAX_MINUTES / 60} 小时；支付类 P0 报错 / 积压 /
                    回调缺失不退避）· 连续 {thresholds?.recovery_minutes ?? 10}{' '}
                    分钟未再出现自动恢复（短暂故障的恢复不单独推送）
                  </div>
                </div>
                <div className="seg">
                  <button
                    type="button"
                    className={tab === 'active' ? 'on' : ''}
                    onClick={() => setTab('active')}
                  >
                    活跃 {overview.alerts ? activeAlerts.length : '—'}
                  </button>
                  <button
                    type="button"
                    className={tab === 'recovered' ? 'on' : ''}
                    onClick={() => setTab('recovered')}
                  >
                    24h 已恢复 {overview.alerts ? recoveredAlerts.length : '—'}
                  </button>
                </div>
              </div>
              <div className="vstack" style={{ padding: '4px 14px 12px', gap: 8 }}>
                {!overview.alerts ? (
                  <div
                    style={{
                      padding: '18px 0',
                      textAlign: 'center',
                      color: 'var(--fg-faint)',
                      fontSize: 12,
                    }}
                  >
                    告警状态在该环境后台查看（本环境 cron 只评估同环境的 C 端服务）
                  </div>
                ) : shownAlerts.length === 0 ? (
                  <div
                    style={{
                      padding: '18px 0',
                      textAlign: 'center',
                      color: 'var(--fg-faint)',
                      fontSize: 12,
                    }}
                  >
                    {tab === 'active' ? '当前无活跃告警' : '近 24h 无已恢复告警'}
                  </div>
                ) : (
                  shownAlerts.map((a) => (
                    <AlertItem
                      key={a.fingerprint}
                      a={a}
                      onLogs={
                        // 降级放行统计查询失败是后台自身告警，没有可下钻的 C 端日志
                        a.latency ||
                        a.fingerprint.startsWith(
                          `${SERVER_MONITOR_RATE_LIMIT_DEGRADED_QUERY_FAILED_EVENT}|`,
                        )
                          ? undefined
                          : () => showAlertLogs(a)
                      }
                    />
                  ))
                )}
              </div>
            </div>

            {/* 规则 + 通道 */}
            <div className="vstack" style={{ gap: 12, minWidth: 0 }}>
              <div className="card" style={{ overflow: 'hidden' }}>
                <div
                  className="hstack"
                  style={{ justifyContent: 'space-between', padding: '12px 14px' }}
                >
                  <div>
                    <span style={{ fontWeight: 500, fontSize: 13.5 }}>告警规则</span>
                    <div style={{ color: 'var(--fg-muted)', fontSize: 11.5, marginTop: 2 }}>
                      {overview.rules.thresholds_source === 'environment'
                        ? '规则随代码发布 · 阈值由环境变量覆盖'
                        : '规则随代码发布 · 该服务不在本环境评估，展示代码默认阈值'}
                    </div>
                  </div>
                  <span className="chip" style={{ fontSize: 10 }}>
                    <Icons.IconLock size={10} />
                    只读
                  </span>
                </div>
                <div className="vstack" style={{ padding: '4px 14px 12px', gap: 8 }}>
                  {toRuleRows(overview).map((r) => (
                    <div
                      key={r.name}
                      className="hstack"
                      style={{
                        gap: 10,
                        padding: '8px 10px',
                        border: '1px solid var(--border)',
                        borderRadius: 6,
                        background: 'var(--bg-elev)',
                      }}
                    >
                      <LevelChip level={r.severity} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="hstack" style={{ gap: 6 }}>
                          <span style={{ fontSize: 12.5, fontWeight: 500 }}>{r.name}</span>
                          {overview.is_monitor_target && (
                            <span
                              className="hstack"
                              style={{ gap: 4, fontSize: 10, color: 'var(--pos)' }}
                            >
                              <span
                                className="pulse-dot"
                                style={{
                                  width: 5,
                                  height: 5,
                                  borderRadius: 999,
                                  background: 'var(--pos)',
                                }}
                              />
                              评估中
                            </span>
                          )}
                        </div>
                        <div
                          className="mono"
                          style={{ fontSize: 10.5, color: 'var(--fg-muted)', marginTop: 2 }}
                        >
                          {r.condition}
                        </div>
                        <div style={{ fontSize: 10.5, color: 'var(--fg-faint)', marginTop: 1 }}>
                          {r.note}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="card" style={{ padding: '4px 14px 10px' }}>
                <Section title="通知与自检" pad="0">
                  {notification ? (
                    <>
                      <Row
                        k="飞书群"
                        v={
                          <span className="hstack" style={{ gap: 6 }}>
                            {notification.groupLabel}
                            {!notification.feishuConfigured && (
                              <span
                                className="chip chip-warn"
                                style={{ fontSize: 9.5, padding: '0 5px' }}
                              >
                                未配置
                              </span>
                            )}
                          </span>
                        }
                        kWidth={96}
                      />
                      <Row
                        k="最近推送"
                        v={
                          notification.lastPush ? (
                            <span className="hstack" style={{ gap: 6 }}>
                              <span
                                className={`chip ${notification.lastPush.ok ? 'chip-pos' : 'chip-neg'}`}
                                style={{ fontSize: 9.5, padding: '0 5px' }}
                              >
                                {notification.lastPush.ok ? '成功' : '失败'}
                              </span>
                              <span className="mono">{notification.lastPush.at}</span>
                            </span>
                          ) : (
                            '—'
                          )
                        }
                        kWidth={96}
                      />
                      <Row
                        k="日志拉取"
                        v={
                          notification.lastPull ? (
                            <span className="hstack" style={{ gap: 6 }}>
                              <span
                                className={`chip chip-${notification.lastPull.tone}`}
                                style={{ fontSize: 9.5, padding: '0 5px' }}
                              >
                                {notification.lastPull.label}
                              </span>
                              <span className="mono">{notification.lastPull.detail}</span>
                            </span>
                          ) : (
                            '—'
                          )
                        }
                        kWidth={96}
                      />
                      {paymentLatency && (
                        <>
                          <Row
                            k="支付时效"
                            v={
                              <span className="hstack" style={{ gap: 6 }}>
                                <span
                                  className={`chip ${paymentLatency.tone === 'default' ? '' : `chip-${paymentLatency.tone}`}`}
                                  style={{ fontSize: 9.5, padding: '0 5px' }}
                                >
                                  {paymentLatency.label}
                                </span>
                                <span className="mono">{paymentLatency.detail}</span>
                              </span>
                            }
                            kWidth={96}
                          />
                          {paymentLatency.providers.map((p) => (
                            <Row
                              key={p.provider}
                              k={p.label}
                              v={<span className="mono">{p.text}</span>}
                              kWidth={96}
                            />
                          ))}
                        </>
                      )}
                      <Row
                        k="自身异常"
                        v="日志拉取失败 / token 失效 / 支付时效查询失败时也会推送「监控自身异常」"
                        kWidth={96}
                        last
                      />
                    </>
                  ) : (
                    <Row
                      k="飞书群"
                      v={`${feishuGroupName(overview.service)} · 告警状态在该环境后台查看`}
                      kWidth={96}
                      last
                    />
                  )}
                </Section>
              </div>
            </div>
          </div>

          {/* 接口时效 */}
          {apiLatency && (
            <div
              className="card"
              style={{ overflow: 'hidden' }}
              data-testid="server-monitor-api-latency"
            >
              <div
                className="hstack"
                style={{
                  justifyContent: 'space-between',
                  padding: '10px 14px',
                  gap: 8,
                  flexWrap: 'wrap',
                  borderBottom: '1px solid var(--border)',
                }}
              >
                <div>
                  <span style={{ fontWeight: 500, fontSize: 13.5 }}>接口时效</span>
                  <div style={{ color: 'var(--fg-muted)', fontSize: 11.5, marginTop: 2 }}>
                    {apiLatency.windowLabel} · p95 最慢的接口（前 20）与队列 · 接口取
                    request.complete 日志 latencyMs，队列取批处理 wallTime
                  </div>
                </div>
                <span className="hstack" style={{ gap: 6 }}>
                  <span
                    className={`chip ${apiLatency.tone === 'default' ? '' : `chip-${apiLatency.tone}`}`}
                    style={{ fontSize: 9.5, padding: '0 5px' }}
                  >
                    {apiLatency.label}
                  </span>
                  <span className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                    {apiLatency.detail}
                  </span>
                </span>
              </div>
              {apiLatency.warnings.map((w) => (
                <div key={w} style={{ fontSize: 11, color: 'var(--warn)', padding: '6px 14px 0' }}>
                  {w}
                </div>
              ))}
              {apiLatency.emptyNote ? (
                <div
                  style={{
                    padding: 24,
                    textAlign: 'center',
                    color: 'var(--fg-faint)',
                    fontSize: 12,
                  }}
                >
                  {apiLatency.emptyNote}
                </div>
              ) : (
                <Table className="tbl" style={{ tableLayout: 'auto' }}>
                  <thead>
                    <tr>
                      <th>接口</th>
                      <th style={{ width: 76 }}>类别</th>
                      <th className="num" style={{ width: 70 }}>
                        次数
                      </th>
                      <th className="num" style={{ width: 76 }}>
                        p50
                      </th>
                      <th className="num" style={{ width: 76 }}>
                        p95
                      </th>
                      <th className="num" style={{ width: 76 }}>
                        p99
                      </th>
                      <th style={{ width: 84 }}>级别</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apiLatency.rows.map((r) => (
                      <tr key={r.key} title={r.title}>
                        <td className="mono" style={{ fontSize: 11.5 }}>
                          {r.trigger}
                        </td>
                        <td>
                          <CatChip label={r.categoryLabel} />
                        </td>
                        <td className="num mono" style={{ fontSize: 11.5 }}>
                          {r.countLabel}
                        </td>
                        <td className="num mono" style={{ fontSize: 11.5 }}>
                          {r.p50}
                        </td>
                        <td
                          className="num mono"
                          style={{
                            fontSize: 11.5,
                            fontWeight: r.severity ? 600 : undefined,
                            color: r.severity ? 'var(--fg-strong)' : undefined,
                          }}
                        >
                          {r.p95}
                        </td>
                        <td className="num mono" style={{ fontSize: 11.5 }}>
                          {r.p99}
                        </td>
                        <td>
                          {r.severity ? (
                            <LevelChip level={r.severity} />
                          ) : (
                            <span style={{ fontSize: 11, color: 'var(--fg-faint)' }}>
                              {r.note || '—'}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          )}

          {/* 疑似爬取用户：接口出错时显示错误块（不让卡片静默消失）；刷新失败保留旧数据并标注 */}
          {!userCrawl && userCrawlQ.isError && (
            <div
              className="card"
              style={{ overflow: 'hidden' }}
              data-testid="server-monitor-user-crawl"
            >
              <ErrorBlock
                minHeight={120}
                title="疑似爬取用户加载失败"
                note={
                  userCrawlQ.error?.message ?? 'GET /api/v1/server-monitor/user-crawl 失败 · 请重试'
                }
                requestId={
                  userCrawlQ.error instanceof AdminApiError ? userCrawlQ.error.requestId : undefined
                }
                onRetry={() => void userCrawlQ.refetch()}
              />
            </div>
          )}
          {userCrawl && (
            <div
              className="card"
              style={{ overflow: 'hidden' }}
              data-testid="server-monitor-user-crawl"
            >
              <div
                className="hstack"
                style={{
                  justifyContent: 'space-between',
                  padding: '12px 14px',
                  gap: 8,
                  flexWrap: 'wrap',
                  borderBottom: '1px solid var(--border)',
                }}
              >
                <div>
                  <span style={{ fontWeight: 500, fontSize: 13.5 }}>疑似爬取用户</span>
                  <div style={{ color: 'var(--fg-muted)', fontSize: 11.5, marginTop: 2 }}>
                    {userCrawl.ruleLabel}
                  </div>
                </div>
                <span className="hstack" style={{ gap: 6 }}>
                  <span
                    className={`chip ${userCrawl.tone === 'default' ? '' : `chip-${userCrawl.tone}`}`}
                    style={{ fontSize: 9.5, padding: '0 5px' }}
                  >
                    {userCrawl.label}
                  </span>
                  <span className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                    {userCrawl.detail}
                  </span>
                </span>
              </div>
              {userCrawlQ.isError && (
                <div style={{ fontSize: 11, color: 'var(--warn)', padding: '6px 14px 0' }}>
                  最近一次刷新失败，以下为上次加载的数据
                </div>
              )}
              {userCrawl.warnings.map((w) => (
                <div key={w} style={{ fontSize: 11, color: 'var(--warn)', padding: '6px 14px 0' }}>
                  {w}
                </div>
              ))}
              {userCrawl.emptyNote ? (
                <div
                  style={{
                    padding: 24,
                    textAlign: 'center',
                    color: 'var(--fg-faint)',
                    fontSize: 12,
                  }}
                >
                  {userCrawl.emptyNote}
                </div>
              ) : (
                <Table className="tbl" style={{ tableLayout: 'auto' }}>
                  <thead>
                    <tr>
                      <th>用户 ID</th>
                      <th className="num" style={{ width: 84 }}>
                        正文请求
                      </th>
                      <th className="num" style={{ width: 70 }}>
                        被拒
                      </th>
                      <th className="num" style={{ width: 70 }}>
                        去重章节
                      </th>
                      <th className="num" style={{ width: 56 }}>
                        书数
                      </th>
                      <th style={{ width: 150 }}>最近命中窗口</th>
                      <th style={{ width: 96 }}>首次发现</th>
                      <th style={{ width: 72 }}>飞书</th>
                      <th style={{ width: 40 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {userCrawl.rows.map((r) => (
                      <tr key={r.key} title={r.title}>
                        <td className="mono" style={{ fontSize: 11.5 }}>
                          {r.userId}
                        </td>
                        <td className="num mono" style={{ fontSize: 11.5, fontWeight: 600 }}>
                          {r.contentRequests}
                        </td>
                        <td className="num mono" style={{ fontSize: 11.5 }}>
                          {r.rejected}
                        </td>
                        <td className="num mono" style={{ fontSize: 11.5 }}>
                          {r.chapters}
                        </td>
                        <td className="num mono" style={{ fontSize: 11.5 }}>
                          {r.novels}
                        </td>
                        <td className="mono" style={{ fontSize: 11 }}>
                          {r.window}
                        </td>
                        <td className="mono" style={{ fontSize: 11 }}>
                          {r.firstSeen}
                        </td>
                        <td>
                          <span
                            className={`chip ${r.status.tone === 'default' ? '' : `chip-${r.status.tone}`}`}
                            style={{ fontSize: 9.5, padding: '0 5px' }}
                          >
                            {r.status.label}
                          </span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-ghost btn-icon btn-sm"
                            aria-label="复制追查命令"
                            title="复制追查命令"
                            onClick={() => {
                              void navigator.clipboard?.writeText(r.command);
                              toast('已复制追查命令', { tone: 'pos', icon: 'IconCopy' });
                            }}
                          >
                            <Icons.IconCopy size={12} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          )}
        </>
      )}

      {/* 错误日志 */}
      <div className="card" style={{ overflow: 'hidden' }} ref={logsRef}>
        <div
          className="hstack"
          style={{
            justifyContent: 'space-between',
            padding: '10px 14px',
            gap: 8,
            flexWrap: 'wrap',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <div className="hstack" style={{ gap: 8 }}>
            <span style={{ fontWeight: 500, fontSize: 13.5 }}>错误日志</span>
            <span style={{ color: 'var(--fg-faint)', fontSize: 11 }}>
              Cloudflare Workers Logs · 保留 7 天
            </span>
          </div>
          <div className="hstack" style={{ gap: 8 }}>
            <div className="seg">
              {SERVER_MONITOR_LOG_RANGES.map((r) => (
                <button
                  type="button"
                  key={r}
                  className={range === r ? 'on' : ''}
                  onClick={() => setRange(r)}
                >
                  {r}
                </button>
              ))}
            </div>
            <FilterButton
              label="级别"
              value={lvF}
              onChange={(v) => setLvF(v as ServerMonitorLogLevel[])}
              options={LEVEL_OPTIONS}
              width={150}
            />
            <FilterButton
              label="类别"
              value={catF}
              onChange={(v) => setCatF(v as ServerMonitorCategory[])}
              options={CATEGORY_OPTIONS}
              width={150}
            />
            <SearchInput
              width={240}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="event / 路径 / requestId / errorCode"
            />
          </div>
        </div>
        {logsUnconfigured ? (
          <EmptyBlock
            minHeight={160}
            title="日志查询未配置"
            note="admin Worker 未配置 Workers Observability Telemetry token，无法查询 C 端错误日志。"
          />
        ) : logsPending ? (
          <TableSkeleton rows={6} cols={8} checkbox={false} />
        ) : logsError ? (
          <ErrorBlock
            minHeight={160}
            title="错误日志加载失败"
            note={logsError.message}
            requestId={logsError instanceof AdminApiError ? logsError.requestId : undefined}
            onRetry={() => void logsQ.refetch()}
          />
        ) : logRows.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--fg-faint)', fontSize: 12 }}>
            当前筛选范围内没有错误日志
          </div>
        ) : (
          <Table className="tbl" style={{ tableLayout: 'auto' }}>
            <thead>
              <tr>
                <th style={{ width: 110 }}>时间</th>
                <th style={{ width: 60 }}>级别</th>
                <th style={{ width: 76 }}>类别</th>
                <th>event</th>
                <th>errorCode</th>
                <th className="num" style={{ width: 56 }}>
                  HTTP
                </th>
                <th>路由</th>
                <th>requestId</th>
              </tr>
            </thead>
            <tbody>
              {logRows.map((r) => (
                <tr key={r.id} onClick={() => setDrawer(r)} style={{ cursor: 'pointer' }}>
                  <td className="mono" style={{ fontSize: 11.5 }}>
                    {r.timeLabel}
                  </td>
                  <td>
                    <LvChip lv={r.level} />
                  </td>
                  <td>
                    <CatChip label={r.categoryLabel} />
                  </td>
                  <td className="mono" style={{ fontSize: 11.5 }}>
                    {r.event}
                  </td>
                  <td className="mono" style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>
                    {r.errorCode}
                  </td>
                  <td className="num mono" style={{ fontSize: 11.5 }}>
                    {r.httpStatus ?? '—'}
                  </td>
                  <td className="mono" style={{ fontSize: 11.5 }}>
                    {r.route}
                  </td>
                  <td className="mono" style={{ fontSize: 11, color: 'var(--fg-faint)' }}>
                    {r.requestIdShort}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        {!logsUnconfigured && !logsError && !logsPending && (
          <Pagination
            total={0}
            totalMode="cursor"
            page={page}
            pageSize={SERVER_MONITOR_LOG_PAGE_LIMIT}
            pageItemCount={logRows.length}
            hasMore={nextCursor !== null}
            onChange={changePage}
          />
        )}
      </div>

      {drawer && service && (
        <LogDrawer
          row={drawer}
          command={buildLogQueryCommand(drawer, service)}
          dashboardUrl={overview?.dashboard_logs_url ?? null}
          onClose={() => setDrawer(null)}
        />
      )}
    </div>
  );
}
